import {
  CompanyLifecycleStatus,
  CompanyLifecycleSubStatus,
} from '../../interfaces/companyLifecycle'
import { DEAL_STAGE_LADDER } from '../../interfaces/deal'
import { MY_RECORDS_QUICK_FILTER, dealOpenStageSql } from './prospect'
import { FilterFieldType, IFilter } from './types'

/** A column of a company's HQ, its oldest address; CompaniesList joins the HQ by this same pick. */
export const companyHqAddressSql = (column: 'id' | 'city' | 'state'): string =>
  `(SELECT a.${column} FROM address a WHERE a.company_id = "company".id ORDER BY a.created_at NULLS FIRST, a.id LIMIT 1)`

// The derived company.lifecycle_status, not the Salesforce company.status mirror.
const COMPANY_STATUS_VALUES = Object.values(CompanyLifecycleStatus)

export const COMPANIES_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    alias: 'company',
    type: FilterFieldType.Text,
    title: 'Company',
    placeholder: 'Company',
    isSortable: true,
    // Folded into the top search box so typing a company name matches it (the
    // name is the primary thing users search by).
  },
  {
    field: 'lifecycleStatus',
    alias: 'company',
    type: FilterFieldType.List,
    title: 'Status',
    isSortable: true,
    options: COMPANY_STATUS_VALUES.map((value) => ({ value, label: value })),
  },
  {
    field: 'lifecycleSubStatus',
    alias: 'company',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Sub-status',
    placeholder: 'Sub-status',
    options: Object.values(CompanyLifecycleSubStatus).map((value) => ({ value, label: value })),
  },
  {
    // Shown only on the Radar, where Tracking companies are worked by it.
    field: 'revisitOn',
    alias: 'company',
    type: FilterFieldType.Date,
    title: 'Revisit',
    align: 'right',
    isSortable: true,
    excludeColumn: true,
  },
  {
    // The stage of the company's open deal.
    field: 'dealStage',
    dbField: dealOpenStageSql('deal'),
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Stage',
    placeholder: 'Stage',
    options: DEAL_STAGE_LADDER.map((stage) => ({ value: stage, label: stage })),
  },
  {
    // Sorts by the owner's first name (owner.first_name via the joined user) and
    // renders as the owner avatar + first name, like the activity-queue assignee.
    field: 'firstName',
    alias: 'owner',
    type: FilterFieldType.Text,
    title: 'Owner',
    isSortable: true,
    excludeFilter: true,
  },
  {
    // Owner multi-select filter (by user id) — options injected client-side from
    // the users list. Not a column (the owner column above renders avatar + name).
    field: 'ownerId',
    dbField: 'owner_id',
    alias: 'company',
    type: FilterFieldType.Autocomplete,
    isMultiple: true,
    title: 'Owner',
    placeholder: 'Owner',
    excludeColumn: true,
  },
  {
    // Options are the Salesforce industry picklist, injected client-side.
    field: 'industryType',
    alias: 'company',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Industry',
    placeholder: 'Industry',
    isSortable: true,
  },
  {
    field: 'industrySubtype',
    alias: 'company',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Sub-industry',
    placeholder: 'Sub-industry',
    isSortable: true,
  },
  {
    // Sorts + filters by the linked client_info's max line amount
    // (clientInfo.max_line_amount); rendered as a min/max range in the menu.
    field: 'maxLineAmount',
    alias: 'clientInfo',
    type: FilterFieldType.Amount,
    title: 'Commitment / Line',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'lastInteractionDate',
    alias: 'company',
    type: FilterFieldType.Date,
    title: 'Last Activity',
    align: 'right',
    isSortable: true,
    // Render the date range as the top-right picker (like every other table)
    // rather than folding it into the filter menu.
    isExternalField: true,
  },
  {
    field: 'hq',
    type: FilterFieldType.Text,
    title: 'HQ',
    isCustom: true,
    excludeFilter: true,
  },
  {
    // Options are the US states, injected client-side.
    field: 'hqState',
    dbField: companyHqAddressSql('state'),
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'HQ State',
    placeholder: 'HQ State',
    excludeColumn: true,
  },
  {
    field: 'hqCity',
    dbField: companyHqAddressSql('city'),
    type: FilterFieldType.Text,
    title: 'HQ City',
    placeholder: 'HQ City',
    excludeColumn: true,
    excludeFullSearchFilter: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Radar',
        filters: { lifecycleStatus: CompanyLifecycleStatus.Radar },
      },
      {
        title: 'Prospects',
        filters: { lifecycleStatus: CompanyLifecycleStatus.ActiveProspect },
      },
      {
        title: 'Diligence',
        filters: { lifecycleStatus: CompanyLifecycleStatus.DueDiligence },
      },
      {
        title: 'Clients',
        filters: { lifecycleStatus: CompanyLifecycleStatus.Current },
      },
      {
        title: 'Past',
        filters: {
          lifecycleStatus: CompanyLifecycleStatus.Radar,
          lifecycleSubStatus: [CompanyLifecycleSubStatus.PastClient],
        },
      },
    ],
  },
] as const satisfies IFilter[]

/**
 * The whole Radar is tracked, so its sub-status reads as where the company came from: never
 * engaged, a past client, or a prior deal that closed and is being tracked.
 */
export const RADAR_TYPE_LABELS: Partial<Record<CompanyLifecycleSubStatus, string>> = {
  [CompanyLifecycleSubStatus.New]: 'New',
  [CompanyLifecycleSubStatus.PastClient]: 'Past client',
  [CompanyLifecycleSubStatus.Tracking]: 'Prior deal',
}

const RADAR_TYPE_OPTIONS = Object.entries(RADAR_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

/** The company columns a Radar row shows, with its revisit date beside its type. */
function radarColumns() {
  return COMPANIES_LIST_FILTERS_CONFIG.filter(
    ({ field }) =>
      !['lifecycleStatus', 'dealStage', 'maxLineAmount', 'quickFilter'].includes(field),
  ).map((filter) => {
    if (filter.field === 'revisitOn') return { ...filter, excludeColumn: false }
    if (filter.field === 'lifecycleSubStatus') {
      return { ...filter, title: 'Type', placeholder: 'Type', options: RADAR_TYPE_OPTIONS }
    }
    return filter
  })
}

/**
 * Companies on the Radar: no open deal, so no stage or line. The lifecycle status is fixed
 * by the caller; the revisit date is what a Tracking company is worked by.
 */
export const RADAR_COMPANIES_LIST_FILTERS_CONFIG = [
  ...radarColumns(),
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [{ title: MY_RECORDS_QUICK_FILTER, filters: { ownerId: [] as string[] } }],
  },
] satisfies IFilter[]

// CRM organization record types (synced from non-Client Salesforce Accounts).
export const ORGANIZATION_TYPE_VALUES = [
  'Vendor',
  'Investor',
  'Current Lender',
  'Referral Source',
] as const

// Derived relationship roles — the categories deriveOrganizationRoles produces from
// the linked entity's financial flags (debtor/creditor/investor/lender). These are
// the meaningful, filterable roles (unlike the free-form Salesforce `type` string),
// so the Role(s) filter maps each value to its entity flag in OrganizationsList.
export const ORGANIZATION_ROLE_VALUES = [
  'Customer',
  'Vendor',
  'Investor',
  'Current Lender',
] as const

// Salesforce organization sub-type picklist. Mirrors the distinct sub_type values
// synced from Salesforce; extend if Salesforce adds a sub-type (the column still
// displays unknown values — only the filter dropdown is bounded by this list).
export const ORGANIZATION_SUB_TYPE_VALUES = [
  '3PL/Fulfillment',
  'ABL',
  'Accelerator',
  'Accountant',
  'Advertising',
  'Appraiser/Auctioneer',
  'Attorney (Bankruptcy)',
  'Attorney (Growth)',
  'Board Member/Advisor',
  'Broker',
  'Business Banking',
  'Business Consultant',
  'Business Intelligence',
  'Cash Flow',
  'Certification',
  'Co-man',
  'Commercial Lending',
  'Content Management',
  'Contract Lab & Testing',
  'Credit Card',
  'Customer Support',
  'Dwight Legal',
  'eCommerce Platform',
  'Equipment Finance',
  'ERP',
  'Factor',
  'Field Exam',
  'Finance',
  'Freight',
  'Fundless Sponsor',
  'Growth Equity',
  'Human Capital Management',
  'Influencer Marketing',
  'Insurance Broker',
  'Investment Bank',
  'Lender Finance',
  'Marketing',
  'Marketplace',
  'MCA',
  'Media',
  'Mez',
  'Ops Consultant',
  'Other',
  'Outsourced CFO',
  'Outsourced IT',
  'Outsourced Sales',
  'Packaging',
  'Payment Processor',
  'Performance Consultant',
  'Performance Management',
  'Podcasts',
  'Point of Sale Marketing',
  'Private Equity',
  'PR/Marketing Agency',
  'Raw Materials',
  'Real Estate',
  'Recruiter',
  'Returns Management',
  'Revenue Based Lender',
  'SPAC',
  'Strategic Acquirer',
  'Supply Chain Management',
  'Talent Agency',
  'Tax',
  'Trade Finance',
  'Trade Org',
  'Turnaround Consultant',
  'Unknown',
  'Venture',
  'Venture Debt',
  'Venture or Seed',
  'Wealth Management',
  'Workout',
] as const

export const ORGANIZATIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    alias: 'organization',
    type: FilterFieldType.Text,
    title: 'Organization',
    placeholder: 'Organization',
    isSortable: true,
    // Folded into the top search box so typing an organization name matches it
    // (the name is the primary thing users search by).
  },
  {
    // Derived role — not a stored column, so isCustom (OrganizationsList maps each
    // selected role to the linked entity's financial flag). List/isMultiple so it
    // surfaces as a multi-select in the funnel (a Text filter would be hidden).
    field: 'roles',
    type: FilterFieldType.List,
    title: 'Role(s)',
    isMultiple: true,
    isCustom: true,
    options: ORGANIZATION_ROLE_VALUES.map((value) => ({ value, label: value })),
  },
  {
    field: 'type',
    alias: 'organization',
    type: FilterFieldType.List,
    title: 'Type',
    isSortable: true,
    options: ORGANIZATION_TYPE_VALUES.map((value) => ({ value, label: value })),
  },
  {
    // List/isMultiple so Sub-type is a filterable multi-select in the funnel
    // (a Text filter is folded into full-search and never shown in the menu).
    field: 'subType',
    alias: 'organization',
    type: FilterFieldType.List,
    title: 'Sub-type',
    isMultiple: true,
    isSortable: true,
    options: ORGANIZATION_SUB_TYPE_VALUES.map((value) => ({ value, label: value })),
  },
  {
    // Count of related companies — grounded in SQL by OrganizationsList (a
    // correlated count). isCustom → the generic query defers to the service.
    field: 'relatedCompanies',
    type: FilterFieldType.Number,
    title: 'Related Companies',
    align: 'right',
    isCustom: true,
    isSortable: true,
  },
  {
    // Filters/sorts by the linked capital info's fund size (capitalInfo.size).
    field: 'size',
    alias: 'capitalInfo',
    type: FilterFieldType.Amount,
    title: 'Fund Profile',
    align: 'right',
    isSortable: true,
  },
  // Portfolio-exposure totals for the linked legacy entity, read from the OpenSearch
  // entity index by OrganizationsList (excludeFilter → display-only columns; not
  // sortable because the Postgres list query has no access to the indexed values).
  {
    field: 'totalAr',
    type: FilterFieldType.Amount,
    title: 'Total AR',
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'totalAp',
    type: FilterFieldType.Amount,
    title: 'Total AP',
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'totalExposure',
    type: FilterFieldType.Amount,
    title: 'Total Exposure',
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'dso',
    type: FilterFieldType.Number,
    title: 'Avg DSO',
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'lastInteractionDate',
    alias: 'organization',
    type: FilterFieldType.Date,
    title: 'Last Activity',
    align: 'right',
    isSortable: true,
    // Render the date range as the top-right picker (like every other table)
    // rather than folding it into the filter menu.
    isExternalField: true,
  },
  // Toggle: when true the list also includes legacy entity_info records that
  // have no linked CRM organization. Handled entirely in the service (it picks
  // the unified query path) — isCustom so buildFiltersQuery never treats it as a
  // real column (there is no organization.include_entities).
  {
    field: 'includeEntities',
    type: FilterFieldType.Boolean,
    excludeColumn: true,
    isCustom: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      { title: 'Vendors', filters: { type: 'Vendor' } },
      { title: 'Investors', filters: { type: 'Investor' } },
      { title: 'Lenders', filters: { type: 'Current Lender' } },
      { title: 'Customers', filters: { includeEntities: 'true' } },
    ],
  },
] as const satisfies IFilter[]

// Derived person "type" — surfaced from the primary relationship role until the
// Editable Records ticket adds a first-class role vocabulary + isPrimary flag.
export const PERSON_TYPE_VALUES = [
  'Contact',
  'Executive',
  'Board',
  'Reference',
  'Investor',
  'Advisor',
] as const

export const PEOPLE_LIST_FILTERS_CONFIG = [
  {
    // Ordered/filtered by last name; the frontend renders the full name + avatar.
    field: 'lastName',
    alias: 'person',
    type: FilterFieldType.Text,
    title: 'Name',
    isSortable: true,
  },
  {
    // First name isn't its own column (the Name column shows the full name), but
    // it must be searchable — otherwise a first-name search (e.g. "Alex") only
    // matches people whose first name happens to appear in their email. Included
    // in full-text search via getAllFiltersColumns; hidden as a column.
    field: 'firstName',
    alias: 'person',
    type: FilterFieldType.Text,
    title: 'First name',
    excludeColumn: true,
  },
  {
    // Derived from the primary relationship's role (grounded in SQL by PeopleList
    // for sort/filter). isCustom → the generic query skips it; the service handles it.
    field: 'primaryTitle',
    type: FilterFieldType.Text,
    title: 'Title / Role',
    isCustom: true,
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    // Derived from the primary relationship's company/org name (grounded in SQL).
    field: 'primaryAffiliation',
    type: FilterFieldType.Text,
    title: 'Company / Organization',
    isCustom: true,
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    // Derived person type (grounded in SQL by PeopleList via a role CASE).
    field: 'type',
    type: FilterFieldType.List,
    title: 'Type',
    isCustom: true,
    isSortable: true,
    isMultiple: true,
    options: PERSON_TYPE_VALUES.map((value) => ({ value, label: value })),
  },
  {
    field: 'emailAddress',
    alias: 'person',
    type: FilterFieldType.Text,
    title: 'Email',
    isSortable: true,
  },
  {
    field: 'phoneNumber',
    alias: 'person',
    type: FilterFieldType.Text,
    title: 'Phone',
    isSortable: true,
  },
  {
    // Sorts by the owner's first name (owner.first_name via the joined user) and
    // renders as the owner avatar + first name, like the activity-queue assignee.
    field: 'firstName',
    alias: 'owner',
    type: FilterFieldType.Text,
    title: 'Owner',
    isSortable: true,
    excludeFilter: true,
  },
  {
    // Owner multi-select filter (by user id) — options injected client-side from
    // the users list. Not a column (the owner column above renders avatar + name).
    field: 'ownerId',
    dbField: 'owner_id',
    alias: 'person',
    type: FilterFieldType.Autocomplete,
    isMultiple: true,
    title: 'Owner',
    placeholder: 'Owner',
    excludeColumn: true,
  },
  {
    field: 'lastInteractionDate',
    alias: 'person',
    type: FilterFieldType.Date,
    title: 'Activity Date',
    align: 'right',
    isSortable: true,
    // Render the date range as the top-right picker (like every other table)
    // rather than folding it into the filter menu.
    isExternalField: true,
  },
] as const satisfies IFilter[]
